import { currentAxisWeight } from "@shared/labs/axis-weights";
import type {
  CorrectionCallback,
  Evidence,
  ThreeAxisRecommendation,
} from "@shared/labs/three-axis";
import { useEffect, useRef, useState } from "react";
import { AjustarPanel } from "./ajustar-panel";
import { formatScoresAsDots, humanizePredicate } from "./format-scores";

const AXIS_LABEL = { gca: "GCA", compWay: "Comp Way", skills: "Skills" } as const;

export type OnCorrectionCallback = CorrectionCallback;

export interface EvidencesSectionProps {
  rec: ThreeAxisRecommendation;
  onCorrection?: OnCorrectionCallback;
}

export function EvidencesSection({ rec, onCorrection }: EvidencesSectionProps) {
  const adjustedCount = rec.evidences.filter((e) => e.chroCorrection).length;
  const total = rec.evidences.length;
  return (
    <div className="card evidences">
      <div className="card-head">
        <div className="card-head-left">
          <h3>Evidências</h3>
          <div className="sub">
            Sinais que decidiram cada pontuação. Cada evidência se ancora numa rubrica de 5 níveis e
            cita fontes do perfil.
          </div>
        </div>
        <span className={`pill ${adjustedCount > 0 ? "amber" : "forest"}`}>
          {adjustedCount > 0
            ? `${total - adjustedCount} cumpridas · ${adjustedCount} ajustada${adjustedCount === 1 ? "" : "s"}`
            : `${total} cumpridas`}
        </span>
      </div>
      <div className="ev-list">
        {rec.evidences.map((ev, i) => (
          <EvidenceRow key={ev.id} ev={ev} onCorrection={onCorrection} defaultOpen={i === 0} />
        ))}
      </div>
    </div>
  );
}

function EvidenceRow({
  ev,
  onCorrection,
  defaultOpen = false,
}: {
  ev: Evidence;
  onCorrection?: OnCorrectionCallback;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [adjusting, setAdjusting] = useState(false);
  const corrected = ev.chroCorrection;
  const displayScore = corrected?.correctedScore ?? ev.score ?? 0;
  const adjustPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (adjusting && adjustPanelRef.current) {
      adjustPanelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [adjusting]);
  // Strength pill — derived from displayScore. Mirrors the mockup
  // (Forte / Ajustada / Sem citação) but keyed off the actual numeric score.
  const strength: { tone: "forest" | "amber" | "muted"; label: string } = corrected
    ? { tone: "amber", label: "Ajustada" }
    : displayScore >= 4
      ? { tone: "forest", label: "Forte" }
      : displayScore >= 2.5
        ? { tone: "amber", label: "Parcial" }
        : { tone: "muted", label: "Sem sinal" };

  // Weights mini-line under title — keeps the per-axis weight info that
  // .ev-chips used to surface, in a single muted strip.
  const weightLine = ev.contributions
    .map((c) => {
      const w = c.axis === "skills" ? c.weight : currentAxisWeight(c.axis, ev.id) || c.weight;
      return `${AXIS_LABEL[c.axis]} ×${w.toFixed(2)}`;
    })
    .join(" · ");

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: interactive container with nested interactive elements cannot be a native button */}
      <div
        className={`ev-row ledger-row ${open ? "open" : ""} ${corrected ? "has-adjust" : ""}`.trim()}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((o) => !o);
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <div>
          <div className="title">
            <span className={`ev-caret ${open ? "open" : ""}`} aria-hidden>
              <svg viewBox="0 0 14 14" width="12" height="12" focusable="false" role="presentation">
                <title>chevron</title>
                <path
                  d="M3 5.5 L7 9.5 L11 5.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {ev.name}
          </div>
          <div className="desc">{formatScoresAsDots(humanizePredicate(ev.description))}</div>
          <div className="desc" style={{ marginTop: 4, fontSize: 12 }}>
            {weightLine}
          </div>
          {corrected && (
            <div className="desc" style={{ marginTop: 4 }}>
              <span className="adj-badge">
                {`ajustada · ${corrected.originalScore.toFixed(1)} → ${corrected.correctedScore.toFixed(1)}`}
              </span>
            </div>
          )}
        </div>
        <span className={`pill ${strength.tone}`}>{strength.label}</span>
        <span className="score-cell">
          {displayScore.toFixed(1)} <span className="max">/ 5</span>
        </span>
        {onCorrection ? (
          <button
            type="button"
            className={`ev-action ${corrected ? "done" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setAdjusting(true);
            }}
            aria-label="ajustar"
          >
            Ajustar
          </button>
        ) : (
          <span />
        )}
      </div>
      {open && <EvidenceDetail ev={ev} />}
      {adjusting && onCorrection && (
        <div ref={adjustPanelRef}>
          <AjustarPanel
            kind="evidence"
            target={ev.name}
            current={displayScore}
            scale="0-5"
            allowPreview
            onSave={async (newScore, rationale, { policy }) => {
              await onCorrection({
                evidenceId: ev.id,
                newScore,
                rationale,
                kind: "evidence",
                policy,
              });
              setAdjusting(false);
            }}
            onCancel={() => setAdjusting(false)}
          />
        </div>
      )}
    </>
  );
}

// StarDots removed (DESIGN v8: zero glyphs). Rating now renders as numeric
// chrome "N / 5" in tabular-nums via the .ev-star-num span above.

function QuoteText({ text }: { text: string }) {
  // The LLM marks each source quote with a leading ↑ (positive signal) or ↓
  // (limiting signal). Pull that prefix off and style it so the polarity is
  // readable at a glance without burying the signal in the italic body.
  const match = text.match(/^\s*([↑↓])\s+([\s\S]*)$/);
  if (!match) return <>"{text}"</>;
  const [, arrow, rest] = match;
  const up = arrow === "↑";
  return (
    <>
      <span className={up ? "qt-arrow qt-arrow-up" : "qt-arrow qt-arrow-down"}>{arrow}</span> "
      {rest}"
    </>
  );
}

/**
 * Renders the rubric level as a 5-dot scale instead of an "L1"…"L5" label.
 * The CHRO requested the dot scale because the L-prefix made the rubric
 * look like a separate axis from the score itself, which is also dot-scaled
 * on the row's `mini-stars` strip. Dots make the visual mapping obvious.
 */
function DotScale({ value }: { value: 1 | 2 | 3 | 4 | 5 }) {
  return (
    <span className="lvl-dots" role="img" aria-label={`Nível ${value} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`lvl-dot${n <= value ? " on" : ""}`} aria-hidden="true" />
      ))}
    </span>
  );
}

function EvidenceDetail({ ev }: { ev: Evidence }) {
  return (
    <div className="ev-detail">
      <div className="ev-detail-head">Rubrica de 5 níveis · nível selecionado destacado</div>
      <div className="anchors">
        {ev.rubric.map((r) => (
          <article
            key={r.level}
            className={`anchor ${r.level === ev.selectedLevel ? "selected" : ""}`}
          >
            <div className="lvl">
              <DotScale value={r.level} />
              {r.level === ev.selectedLevel ? (
                <span className="lvl-selected">selecionado</span>
              ) : null}
            </div>
            <div className="tx">{humanizePredicate(r.description)}</div>
          </article>
        ))}
      </div>
      {ev.sources.length > 0 && (
        <>
          <div className="ev-detail-head">Fontes do perfil</div>
          <div className="quotes">
            {ev.sources.map((s) => (
              <div key={`${ev.id}::${s.citation}::${s.quote.slice(0, 24)}`} className="quote">
                <div className="qt">
                  <QuoteText text={s.quote} />
                </div>
                <div className="src-label">{s.citation}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
